import {
  IMarkWordAudioTask,
  IMarkWordAudioTaskItemSession,
  IMarkWordAudioTaskSession,
  tMarkWordAudioAnswer
} from 'common/modules/ReactShared';
import { t } from 'i18n';
import { useCallback, useEffect } from 'react';
import { usePromptContext } from 'students/views/shared/hooks';
import { customMediaQuery } from 'students/views/shared/styled';
import styled from 'styled-components';
import Streak from '../../common/components/Streak';
import { usePlayAnswers, useStreakFactor } from '../../common/hooks';
import PlayTaskScreenSwitcher from '../../common/screens/PlayTaskScreenSwitcher';
import { IPlayTaskProps, tPlayTaskComponentType } from '../types';
import { ISelectAnswerPayload } from './MarkWordAudioItem';
import PlayMarkWordAudioMainScreen from './PlayMarkWordAudioMainScreen';
import bgImage from './assets/markWordAudioBg.svg';

const PlayMarkWordAudioTask: tPlayTaskComponentType<
  IPlayTaskProps<IMarkWordAudioTask, IMarkWordAudioTaskSession>
> = ({ task, taskSession, isCompleting, onFinish, onAnswer, onNext, className }) => {
  const { items, instruction } = task;
  const answersFn = usePlayAnswers<tMarkWordAudioAnswer>();

  const { streakNumber, streakPercent, onTheFirstTry } = useStreakFactor();
  const { makeDirty } = usePromptContext();

  useEffect(() => {
    if (taskSession?.taskItemSessions) {
      answersFn(taskSession.taskItemSessions);
    } else {
      answersFn(
        items.map((item, index) => {
          return {
            id: index,
            completed: false,
            answers: [],
            taskItemId: item.id,
            mistakesCount: 0,
            skipped: false,
            url: ''
          };
        })
      );
    }

    onTheFirstTry(false);
  }, [taskSession, items, onTheFirstTry, answersFn]);

  const updateAnswer = (itemId: number, data: Partial<tMarkWordAudioAnswer[number]>) => {
    const answerIndex = answersFn().findIndex(
      (answer: IMarkWordAudioTaskItemSession) => answer.taskItemId === itemId
    );

    const { mistakesCount, skipped } = data;

    if (answerIndex < 0) return;

    const answer = answersFn()[answerIndex];
    const newAnswerData = { ...answer, ...data };
    answersFn([
      ...answersFn().slice(0, answerIndex),
      newAnswerData,
      ...answersFn().slice(answerIndex + 1)
    ]);

    onAnswer && onAnswer(newAnswerData);

    onTheFirstTry(!(mistakesCount || skipped));
  };

  const handleSelectAnswer = (payload: ISelectAnswerPayload) => {
    const { itemId, mistakesCount, solution } = payload;

    updateAnswer(itemId, {
      mistakesCount,
      answers: solution,
      skipped: false
    });

    makeDirty(true);
  };

  const handleSkip = (itemId: number) => {
    updateAnswer(itemId, {
      skipped: true
    });
  };

  const handleFinishTask = () => {
    onNext && onNext();
  };

  const submitTask = useCallback(() => {
    onFinish && onFinish(answersFn());
  }, [onFinish, answersFn]);

  const handleCompleteTask = (goToFinishScreen: () => void) => () => {
    submitTask();

    goToFinishScreen();
  };

  return (
    <>
      <Streak streakNumber={streakNumber} streakPercent={streakPercent} />
      <PlayTaskScreenSwitcher
        isCompleting={isCompleting}
        onExitFinishScreen={handleFinishTask}
        itemsExist={!!items.length}
        className={className}
        startScreenHeading={
          instruction ||
          t('frontend.lesson_task.tasks.play.mark_word.start_screen.heading')
        }
        taskStatus={taskSession?.status}
        completeTask={submitTask}
        taskColor="#3d1f7d"
      >
        {(goToFinishScreen) => (
          <PlayMarkWordAudioMainScreen
            task={task}
            onSelectAnswer={handleSelectAnswer}
            onComplete={handleCompleteTask(goToFinishScreen)}
            onSkip={handleSkip}
          />
        )}
      </PlayTaskScreenSwitcher>
    </>
  );
};

export default PlayMarkWordAudioTask;

PlayMarkWordAudioTask.backgroundComponent = styled.div`
  background-color: #4c69cb;
  background-image: linear-gradient(180deg, #4c69cb 0%, #3d1f7d 100%);

  ${customMediaQuery('desktop')} {
    background-image: url(${bgImage}), linear-gradient(180deg, #4c69cb 0%, #3d1f7d 100%);
    background-repeat: no-repeat, no-repeat;
    background-position: center;
    background-size: cover, auto;
  }
`;
PlayMarkWordAudioTask.bgColor = '#4c69cb';
